/**
 * Rebranding: how this workspace looks, who its mail comes from, what it says.
 *
 * Four tabs, in the order somebody actually does the work: pick the look, prove
 * the addresses, word the messages, plug in the channels.
 *
 * Two things this screen is careful about.
 *
 * White-label fields stay editable when the plan does not carry the add-on —
 * they are stored either way and simply do not apply, so buying the add-on
 * restores the setup rather than asking the admin to type it again. The banner
 * says so rather than disabling the inputs with no explanation.
 *
 * A sender address whose domain has since been withdrawn is shown as such
 * instead of quietly disappearing: mail from it stops either way, and an admin
 * wondering why payslips went quiet deserves to see the reason on this page.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useBranding } from '../contexts/BrandingContext';


const TABS = [
  { id: 'look', label: 'Look & feel' },
  { id: 'senders', label: 'Sending addresses' },
  { id: 'templates', label: 'Email templates' },
  { id: 'channels', label: 'Channels' },
];

// Mirrors PRODUCT_DEFAULTS in backend/branding_service.py.
const DEFAULT_PALETTE = {
  primary_color: '#3b4fb8',
  primary_hover_color: '#2f3f93',
  secondary_color: '#8b9bef',
  accent_color: '#8b9bef',
  sidebar_color: '#141834',
};

const COLOURS = [
  { key: 'primary_color', label: 'Primary', hint: 'Buttons, links, the active menu item.' },
  { key: 'primary_hover_color', label: 'Primary (hover)', hint: 'The darker press state.' },
  { key: 'secondary_color', label: 'Secondary', hint: 'Supporting brand tone.' },
  { key: 'accent_color', label: 'Accent', hint: 'Highlights and badges.' },
  { key: 'sidebar_color', label: 'Sidebar', hint: 'The navigation rail.' },
];

const ASSETS = [
  { variant: 'light', field: 'logo_url', label: 'Logo', hint: 'Colours are taken from this.' },
  { variant: 'dark', field: 'logo_dark_url', label: 'Logo (dark)', hint: 'For the dark sidebar. Colours are taken from this too.' },
  { variant: 'favicon', field: 'favicon_url', label: 'Favicon', hint: 'The browser tab icon.' },
  { variant: 'login_background', field: 'login_background_url', label: 'Login background', hint: 'Behind the sign-in form.' },
];

const Swatches = ({ palette }) => (
  <div className="flex gap-1.5">
    {['primary_color', 'primary_hover_color', 'accent_color', 'sidebar_color'].map((key) => (
      <span key={key} title={`${key}: ${palette[key]}`}
        className="h-7 w-7 rounded-md border border-black/10 shadow-sm"
        style={{ backgroundColor: palette[key] }} />
    ))}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-foreground">{label}</span>
    {hint ? <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span> : null}
    <div className="mt-1.5">{children}</div>
  </label>
);

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) {
    return (
      <div className="text-xs">
        <span className="text-muted-foreground">{label}: </span>
        <span className="text-amber-600">not configured yet — ask your platform operator</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground w-12 shrink-0">{label}</span>
      <code className="flex-1 min-w-0 truncate rounded bg-background border border-border px-1.5 py-1 font-mono">{value}</code>
      <button
        type="button"
        className="shrink-0 rounded border border-border px-1.5 py-1 hover:bg-muted"
        onClick={() => {
          // `writeText` needs a secure context; it rejects over plain http,
          // which is exactly how somebody self-hosting will first see this.
          navigator.clipboard?.writeText(value)
            .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
            .catch(() => toast.error('Could not copy — select the value and copy it manually'));
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const STEP_STATE = {
  done: { mark: '✓', tone: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300' },
  todo: { mark: '•', tone: 'border-border bg-muted/40 text-muted-foreground' },
};

const Step = ({ n, title, done, children }) => (
  <div className={`rounded-lg border p-3 ${done ? STEP_STATE.done.tone : STEP_STATE.todo.tone}`}>
    <p className="text-xs font-semibold mb-2">
      {done ? STEP_STATE.done.mark : n}. {title}
    </p>
    <div className="space-y-1.5 text-foreground">{children}</div>
  </div>
);

/**
 * Everything a tenant needs to put their own domain live, on one panel.
 *
 * Ownership and pointing are shown as separate steps because they fail for
 * different reasons and are fixed by different records — a single "not
 * verified" leaves an admin with no idea which of the two to go and look at.
 *
 * Exported because the superadmin console shows the same records straight after
 * creating a tenant. `onVerify` is optional there: verification runs against the
 * caller's own workspace, and an operator creating somebody else's has nothing
 * to check yet — so the button is dropped rather than rendered inert.
 */
export const DomainSetup = ({ records, verifiedAt, check, onVerify, verifying }) => {
  const owned = check ? check.ownership?.verified : Boolean(verifiedAt);
  const pointing = check ? check.pointing?.pointing : null;
  const route = records.find((r) => r.type === 'CNAME' || r.type === 'A');
  const txt = records.find((r) => r.type === 'TXT');

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-medium">Put this domain live</p>
        {check?.status === 'live' && (
          <span className="rounded-md border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
            Live
          </span>
        )}
        {onVerify && (
          <button type="button" onClick={onVerify} disabled={verifying}
            className="ml-auto rounded-lg border border-border bg-background px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-60">
            {verifying ? 'Checking DNS…' : 'Check now'}
          </button>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Add both records at your DNS provider
        {onVerify
          ? ', then press Check now.'
          : ', then verify the domain from the workspace’s own Rebranding screen.'}
        {' '}Nothing is needed from us — the certificate is issued automatically on the
        first visit. DNS changes can take up to an hour to spread.
      </p>

      {txt && (
        <Step n={1} title="Prove the domain is yours" done={owned}>
          <CopyRow label="Type" value={txt.type} />
          <CopyRow label="Name" value={txt.name} />
          <CopyRow label="Value" value={txt.value} />
        </Step>
      )}

      {route && (
        <Step n={2} title="Point it here" done={pointing === true}>
          <CopyRow label="Type" value={route.type} />
          <CopyRow label="Name" value={route.name} />
          <CopyRow label="Value" value={route.value} />
          {route.note && <p className="text-[11px] text-muted-foreground pt-1">{route.note}</p>}
        </Step>
      )}

      {check && (
        <div className="text-[11px] space-y-1">
          <p className="font-medium">{check.summary}</p>
          {!owned && check.ownership?.detail && (
            <p className="text-muted-foreground">Step 1: {check.ownership.detail}</p>
          )}
          {owned && pointing === false && check.pointing?.detail && (
            <p className="text-muted-foreground">Step 2: {check.pointing.detail}</p>
          )}
        </div>
      )}
    </div>
  );
};

const RebrandingModule = ({ currentTab = 'look' }) => {
  const { refresh: refreshTheme } = useBranding();
  // The sidebar's submenu chooses the tab; local state takes over after that.
  const [tab, setTab] = useState(TABS.some((t) => t.id === currentTab) ? currentTab : 'look');
  useEffect(() => {
    if (TABS.some((t) => t.id === currentTab)) setTab(currentTab);
  }, [currentTab]);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [domainCheck, setDomainCheck] = useState(null);


  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/branding');
      setState(data);
      setForm({ ...(data.own || {}) });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not load branding');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/branding', form);
      toast.success('Branding saved');
      await load();
      await refreshTheme();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Remove this workspace’s branding and go back to the default?')) return;
    await api.delete('/api/branding');
    toast.success('Branding reset');
    await load();
    await refreshTheme();
  };

  // Checked on demand, never on every request: a DNS lookup in front of the
  // anonymous branding call would put a network round trip before every sign-in
  // page and fail closed whenever a resolver hiccupped.
  const verifyDomain = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post('/api/branding/custom-domain/verify');
      setDomainCheck(data);
      if (data?.status === 'live') toast.success(data.summary);
      else toast.message(data?.summary || 'Not ready yet');
      await load();
      await refreshTheme();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not check the domain');
    } finally {
      setVerifying(false);
    }
  };

  const upload = async (variant, file) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    try {
      // The server derives the palette from the logo and stores it — the rule
      // holds for every client, so there is nothing to compute or offer here.
      const { data } = await api.post(`/api/branding/logo?variant=${variant}`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.palette ? 'Uploaded — colours updated from your logo' : 'Uploaded');
      await load();
      await refreshTheme();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Upload failed');
    }
  };

  if (!state) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const {
    effective, white_label_enabled: whiteLabel, white_label_fields: gated,
    verified_domains: domains, is_platform_default: isPlatformDefault,
    portal_domain: portalDomain, dns_records: dnsRecords,
    custom_domain_verified_at: domainVerifiedAt,
  } = state;

  return (
    <div className="p-6 space-y-5">
      <header className="flex flex-wrap items-start gap-4">
        <div>
          <h2 className="text-xl font-semibold">Rebranding</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            How this workspace looks to everyone in it, and how its email arrives.
            {effective.source === 'inherited'
              ? ' Currently inherited from your head office — saving here gives this location its own.'
              : ''}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={reset}
            className="px-3 py-2 text-sm rounded-lg border border-border hover:bg-muted">
            Reset
          </button>
          <button type="button" onClick={save} disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-60">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {isPlatformDefault && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
          <b className="font-semibold">This is the product’s own brand.</b>{' '}
          The logo, favicon and colours you set here are what every workspace shows
          unless it has set its own — and what the sign-in page shows before anyone
          has signed in. The name, sending addresses and white-label switches below
          stay yours alone and are not handed to anybody else.
        </div>
      )}

      {!whiteLabel && (
        <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm">
          <b className="font-semibold">White labelling is not on this plan.</b>{' '}
          Your logo and colours apply as normal. Hiding platform branding, a custom
          domain and a custom email footer are stored but not applied until the{' '}
          <code className="text-xs">{state.white_label_addon}</code> add-on is active.
        </div>
      )}

      <nav className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm rounded-t-lg -mb-px border-b-2 ${
              tab === t.id ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'look' && (
        <>
        {!effective.logo_url && !effective.logo_dark_url ? (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 flex flex-wrap items-center gap-4">
            <Swatches palette={DEFAULT_PALETTE} />
            <div className="text-sm text-muted-foreground min-w-0">
              No logo yet — the default palette applies. Upload one and the colours
              below are taken from it automatically.
            </div>
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="space-y-4">
            <Field label="Workspace name" hint="Replaces the name in the sidebar and in email.">
              <input className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={form.app_name || ''} placeholder={effective.app_name || ''}
                onChange={(e) => set('app_name', e.target.value)} />
            </Field>

            {COLOURS.map((c) => (
              <Field key={c.key} label={c.label} hint={c.hint}>
                <div className="flex items-center gap-2">
                  <input type="color" aria-label={`${c.label} colour`}
                    className="h-9 w-12 rounded border border-border bg-background"
                    value={form[c.key] || effective[c.key] || '#3b4fb8'}
                    onChange={(e) => set(c.key, e.target.value)} />
                  <input className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                    value={form[c.key] || ''} placeholder={effective[c.key] || ''}
                    onChange={(e) => set(c.key, e.target.value)} />
                </div>
              </Field>
            ))}

            <Field label="Theme" hint="What everyone in this workspace sees by default.">
              <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={form.theme_mode || effective.theme_mode || 'system'}
                onChange={(e) => set('theme_mode', e.target.value)}>
                <option value="system">Follow the device</option>
                <option value="light">Always light</option>
                <option value="dark">Always dark</option>
              </select>
            </Field>
          </section>

          <section className="space-y-4">
            {ASSETS.map((a) => (
              <Field key={a.variant} label={a.label} hint={a.hint}>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-24 rounded-lg border border-border bg-muted grid place-items-center overflow-hidden">
                    {effective[a.field]
                      ? <img src={effective[a.field]} alt={`${a.label} preview`} className="max-h-10 max-w-20 object-contain" />
                      : <span className="text-[10px] text-muted-foreground">none</span>}
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="text-xs"
                    onChange={(e) => upload(a.variant, e.target.files?.[0])} />
                </div>
              </Field>
            ))}

            <div className="pt-2 border-t border-border space-y-4">
              <p className="text-xs text-muted-foreground">
                White label{whiteLabel ? '' : ' — stored now, applied when the add-on is active'}
              </p>
              <Field label="Hide platform branding">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox"
                    checked={Boolean(form.hide_platform_branding)}
                    onChange={(e) => set('hide_platform_branding', e.target.checked)} />
                  Remove “Powered by” from the app and its email
                </label>
              </Field>
              {/* One column, two jobs. For the platform tenant this field is the
                  portal domain every workspace hangs off; for everyone else it is
                  a domain they own. Labelling it the same in both places is how
                  an admin ends up typing the wrong thing into it. */}
              <Field
                label={isPlatformDefault ? 'Portal domain' : 'Custom domain'}
                hint={isPlatformDefault
                  ? `Every workspace answers on <name>.${portalDomain || 'yourdomain.com'}. Changing this moves all of them — point DNS first.`
                  : `A domain you own and have verified. Your workspace already answers on <name>.${portalDomain || 'the portal domain'} with nothing to set up.`}
              >
                <input className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  value={form.custom_domain || ''}
                  placeholder={isPlatformDefault ? (portalDomain || 'webhr.in') : 'hr.yourcompany.com'}
                  onChange={(e) => set('custom_domain', e.target.value)} />
              </Field>

              {/* A tenant sets this up themselves, from their own DNS provider,
                  with nothing asked of us. So the screen has to carry the whole
                  procedure: what to create, the exact values to copy, and which
                  of the two records is still missing. */}
              {!isPlatformDefault && Array.isArray(dnsRecords) && dnsRecords.length > 0 && (
                <DomainSetup
                  records={dnsRecords}
                  verifiedAt={domainVerifiedAt}
                  check={domainCheck}
                  onVerify={verifyDomain}
                  verifying={verifying}
                />
              )}

              <Field label="Email footer" hint="Replaces the default sign-off.">
                <textarea rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  value={form.email_footer_text || ''}
                  onChange={(e) => set('email_footer_text', e.target.value)} />
              </Field>
              <Field label="Support email">
                <input className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  value={form.support_email || ''}
                  onChange={(e) => set('support_email', e.target.value)} />
              </Field>
            </div>
          </section>
        </div>
        </>
      )}

      {tab === 'senders' && <SenderTab domains={domains} />}
      {tab === 'templates' && <TemplateTab />}
      {tab === 'channels' && <ChannelTab />}
    </div>
  );
};

const SenderTab = ({ domains }) => {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({ from_email: '', from_name: '' });

  const load = useCallback(async () => {
    const { data } = await api.get('/api/branding/senders');
    setState(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await api.post('/api/branding/senders', draft);
      setDraft({ from_email: '', from_name: '' });
      toast.success('Address added');
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not add that address');
    }
  };

  if (!state) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-2xl">
        Mail can be sent from any address on a domain this workspace verified during
        onboarding. {domains?.length
          ? <>Verified: {domains.map((d) => <code key={d} className="text-xs mx-1">{d}</code>)}</>
          : <b>No verified domain yet — ask platform support to add one before configuring addresses.</b>}
      </p>

      <div className="flex flex-wrap gap-2 items-end">
        <Field label="Address">
          <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm w-64"
            placeholder={domains?.length ? `payroll@${domains[0]}` : 'payroll@yourcompany.com'}
            value={draft.from_email} onChange={(e) => setDraft({ ...draft, from_email: e.target.value })} />
        </Field>
        <Field label="Display name">
          <input className="rounded-lg border border-border bg-background px-3 py-2 text-sm w-48"
            placeholder="Appable Payroll"
            value={draft.from_name} onChange={(e) => setDraft({ ...draft, from_name: e.target.value })} />
        </Field>
        <button type="button" onClick={add} disabled={!domains?.length}
          className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">
          Add address
        </button>
      </div>

      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="text-left p-3">Address</th><th className="text-left p-3">Name</th>
              <th className="text-left p-3">Status</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {state.items.length === 0 && (
              <tr><td colSpan={4} className="p-4 text-muted-foreground">
                No addresses yet — mail goes out from the address the platform set for this workspace.
              </td></tr>
            )}
            {state.items.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="p-3 font-mono text-xs">{s.from_email}</td>
                <td className="p-3">{s.from_name || '—'}</td>
                <td className="p-3">
                  {s.is_default && <span className="text-xs font-semibold text-primary mr-2">Default</span>}
                  {!s.is_active && <span className="text-xs text-muted-foreground mr-2">Disabled</span>}
                  {!s.domain_still_verified && (
                    <span className="text-xs text-amber-700 dark:text-amber-400">
                      Domain no longer verified — this address is not used
                    </span>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {!s.is_default && (
                    <button type="button" className="text-xs underline mr-3"
                      onClick={async () => { await api.post(`/api/branding/senders/${s.id}/default`); load(); }}>
                      Make default
                    </button>
                  )}
                  <button type="button" className="text-xs underline text-destructive"
                    onClick={async () => {
                      try { await api.delete(`/api/branding/senders/${s.id}`); load(); }
                      catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
                    }}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TemplateTab = () => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/api/branding/templates');
    setState(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!state) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const save = async (item) => {
    try {
      await api.put(`/api/branding/templates/${item.template_key}`, {
        display_name: item.display_name,
        subject_template: item.subject_template,
        body_template: item.body_template,
        sender_id: item.sender_id || null,
        is_active: item.is_active,
      });
      toast.success('Template saved');
      setOpen(null);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save');
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground max-w-2xl">
        Reword any message this workspace sends, and choose which verified address
        it comes from. Anything left alone uses the platform’s wording.
      </p>
      <div className="rounded-lg border border-border divide-y divide-border">
        {state.items.map((item) => (
          <div key={item.template_key} className="p-3">
            <div className="flex items-center gap-3 flex-wrap">
              <b className="text-sm">{item.display_name}</b>
              <code className="text-[11px] text-muted-foreground">{item.template_key}</code>
              {item.is_customised && <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">Customised</span>}
              {item.sender_email && <span className="text-[11px] text-muted-foreground">from {item.sender_email}</span>}
              <button type="button" className="ml-auto text-xs underline"
                onClick={() => setOpen(open === item.template_key ? null : { ...item })}>
                {open?.template_key === item.template_key ? 'Close' : 'Edit'}
              </button>
              {item.is_customised && (
                <button type="button" className="text-xs underline text-destructive"
                  onClick={async () => { await api.delete(`/api/branding/templates/${item.template_key}`); load(); }}>
                  Revert
                </button>
              )}
            </div>

            {open?.template_key === item.template_key && (
              <div className="mt-3 space-y-3">
                <Field label="Subject">
                  <input className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={open.subject_template || ''}
                    onChange={(e) => setOpen({ ...open, subject_template: e.target.value })} />
                </Field>
                <Field label="Body" hint={`Variables: ${(item.variables || []).join(', ') || 'none'}`}>
                  <textarea rows={6} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                    value={open.body_template || ''}
                    onChange={(e) => setOpen({ ...open, body_template: e.target.value })} />
                </Field>
                <Field label="Send from" hint="Leave as default to use the workspace’s default address.">
                  <select className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    value={open.sender_id || ''}
                    onChange={(e) => setOpen({ ...open, sender_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Workspace default</option>
                    {state.senders.map((s) => <option key={s.id} value={s.id}>{s.from_email}</option>)}
                  </select>
                </Field>
                <button type="button" onClick={() => save(open)}
                  className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-medium">
                  Save template
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ChannelTab = () => {
  const [state, setState] = useState(null);
  useEffect(() => { api.get('/api/branding/channels').then(({ data }) => setState(data)); }, []);
  if (!state) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-2xl">
        Third-party channels this workspace can send through. Credentials are
        stored encrypted and never shown again once saved.
      </p>
      {state.catalogue.map((channel) => (
        <section key={channel.key} className="rounded-lg border border-border p-4">
          <div className="flex items-baseline gap-3">
            <b className="text-sm">{channel.label}</b>
            <span className="text-xs text-muted-foreground">{channel.description}</span>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {channel.providers.map((p) => {
              const live = state.configured.find((c) => c.provider === p.key);
              return (
                <div key={p.key} className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2">
                    <b className="text-sm">{p.label}</b>
                    <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full ${
                      live ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {live ? (live.enabled ? 'Connected' : 'Paused') : 'Not set up'}
                    </span>
                  </div>
                  {p.help && <p className="text-[11px] text-muted-foreground mt-1">{p.help}</p>}
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Needs: {[...p.config, ...p.secrets].filter((f) => f.required).map((f) => f.label).join(', ') || 'nothing'}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      <p className="text-xs text-muted-foreground">
        Connections are created and edited under Integrations, which already holds
        the credential store and its audit trail.
      </p>
    </div>
  );
};

export default RebrandingModule;
